import { FC, MouseEvent } from "react";
import { css, styled } from "styled-components";
import { PointerRightLight, PointerRight } from "@fenderdigital/ui/Icons";
import { ContentfulNavigationItem } from "../lib/contentfulTypes";
import { button, colors, font } from "../lib/sharedStyles";

interface StyledLinkProps {
  $brand: string;
  $displayType?: string;
  $hasDescription: boolean;
  $hasImage: boolean;
  $index: number;
  $isSupportNav: boolean;
  $level: number;
}

const appButtonLabelTranslations = {
  de: 'Öffnen',
  en: 'Open',
  es: 'Abrir',
  fr: 'Ouvrir',
  it: 'Apri',
  ja: '開く'
};

const FenderLinkStyles = css<StyledLinkProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 32px;
  ${font('Instrument Sans', 600, '16px', '22px', '0.16px')};

  ${({ $level, $isSupportNav }) => $level == 1 && !$isSupportNav && `
    font-size: 24px;
    line-height: 30px;
    letter-spacing: 0.24px;
  `}

  // No parent padding at level 3 to allow for full width images.
  // Padding needs to be applied on elements individually.
  // > span:first-of-type is targeting the title only
  ${({ $level, $isSupportNav }) => ($level == 3 || ($level == 2 && $isSupportNav)) && `
    padding: 0;
    flex-direction: column;
    align-items: initial;
    justify-content: initial;

    > span {
      padding: 0 32px;
    }

    > span:first-of-type {
      padding-top: 14px;
      padding-bottom: 14px;
    }
  `}

  // Decrease the padding between image and title at level 3.
  ${({ $level, $hasImage, $isSupportNav }) => $hasImage && ($level == 3 || ($level == 2 && $isSupportNav)) && `
    > span:first-of-type {
      padding-top: 0;
    }
  `}
  // Decrease the padding between title and description at level 3.
  ${({ $level, $hasDescription, $isSupportNav }) => $hasDescription && ($level == 3 || ($level == 2 && $isSupportNav)) && `
    > span:first-of-type {
      padding-bottom: 0;
    }
  `}

  // Create more space between drawer heading and first item,
  // except for items with images (should be flush) and support nav.
  ${({ $index, $hasImage, $isSupportNav }) => $index == 0 && !$hasImage && !$isSupportNav && `
    margin-top: 16px;
  `}
  // There is a "gotcha" case with support nav past level 1. Needed for "guides" subnav.
  ${({ $index, $hasImage, $isSupportNav, $level }) => $index == 0 && !$hasImage && $isSupportNav && $level == 2 && `
    margin-top: 16px;
  `}


  ${({ $displayType }) => $displayType == 'App Link' && `
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    grid-template-rows: repeat(2, 1fr);
    padding: 18px 16px 32px;
    align-items: flex-end;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.14px;
    border-top: 1px solid #EFF0F0;

    > span {
      padding: 0;
    }

    > span:nth-of-type(1) {
      grid-area: 1 / 2 / 2 / 5;
      margin-left: 5px;
    }
  `}

  ${({ $displayType }) => $displayType == 'Article' && `
    flex-direction: row;
    align-items: center;
    padding: 32px 16px;
    border-bottom: 1px solid #EFF0F0;

    > span:first-of-type {
      padding: 0 0 0 16px;
    }
  `}

  ${({ $displayType }) => $displayType == 'Button' && (
    button('Black Pill', 'fender')
  )}

  ${({ $displayType }) => $displayType == 'Button' && `
    margin: 32px 16px;
    width: calc(100% - 32px);
    padding-top: 20px;
    padding-bottom: 20px;

    > span:first-of-type {
      padding: 0;
    }
  `}
`;

const JacksonLinkStyles = css<StyledLinkProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  ${font('Palanquin Dark', 600, '16px', 'normal', '0.07812rem')};

  ${({ $level }) => $level == 1 && `
    text-transform: uppercase;
    color: #aaa;
  `}

  ${({ $level }) => $level == 2 && `
    font-weight: 500;
    color: #fff;
    letter-spacing: .0125rem;
  `}

  // Shop All link
  ${({ $level, $index }) => $level == 2 && $index == 0 && `
    color: #666;
  `}
  
  ${({ $level }) => $level == 3 && `
    font-weight: 500;
    color: #939393;
    letter-spacing: normal;
    flex-direction: column;
    align-items: initial;
  `}
`;

const Link = styled.a<StyledLinkProps>`
  text-decoration: none;

  ${({ $brand }) => $brand == 'fender' && FenderLinkStyles}
  ${({ $brand }) => $brand == 'jackson' && JacksonLinkStyles};

  &:not([href]) {
    cursor: pointer;
  }
`;

const Description = styled.span<{ $displayType?: string; }>`
  ${font('Instrument Sans', 400, '14px', '20px', '0.14px')}
  color: ${colors.fender.primaryDark};
  margin: 8px 0 24px;

  ${({ $displayType }) => $displayType == 'App Link' && `
    grid-area: 2 / 2 / 3 / 5;
    margin: 0 0 0 5px;
    font-size: 12px;
    line-height: 18px;
    letter-spacing: 0.12px;
  `}
`;

const Image = styled.img<{ $isProduct: boolean; $displayType?: string; }>`
  margin-bottom: 16px;

  ${({ $isProduct }) => $isProduct && `
    filter: drop-shadow(12px 12px 10px #ccc);
    padding: 40px 32px 16px;
  `}

  ${({ $displayType }) => $displayType == 'App Link' && `
    margin: 0;
    grid-area: 1 / 1 / 3 / 2;
  `}

  ${({ $displayType }) => $displayType == 'Article' && `
    width: 80px;
    margin-bottom: 0;
  `}
`;

const AppLinkButton = styled.div`
  grid-area: 1 / 5 / 3 / 6;
  ${font('Instrument Sans', 600, '16px', '22px', '0.16px')};
  color: ${colors.fender.primaryDark};
  text-align: center;
  padding: 10px 24px;
  border-radius: 24px;
  border: 1px solid ${colors.fender.primaryDark};
  margin-bottom: 8px;
`;

interface Props extends ContentfulNavigationItem {
  brand: string;
  index: number;
  isSupportNav?: boolean;
  level: number;
  onClick: (e: MouseEvent) => void;
}

const MobileMenuLink: FC<Props> = ({ 
  brand, 
  fields: { 
    asset,
    description,
    displayType = '',
    openInNewWindow,
    productImageUrl,
    subItems,
    title,
    url,
    // @ts-ignore: This can be a ContentfulNavigationRow.
    navRowTitle
  },
  index,
  isSupportNav = false,
  level,
  onClick
}) => {
  const linkTarget = (openInNewWindow) ? '_blank' : '_self';
  
  let imageUrl = '';
  let hasProductImage = false;
  // Images are not displayed in level 1.
  if (level > 1) {
    // Prefer asset field over product image.
    if (asset && asset.fields.file?.url) {
      imageUrl = `https:${asset.fields.file.url}`;
    } else if (productImageUrl) {
      imageUrl = productImageUrl;
      hasProductImage = true;
    }
  }

  const titleToRender = (navRowTitle) ? navRowTitle : title;

  const isFmicAssetsImage = (url: string): boolean => {
    return url.includes('www.fmicassets.com');
  };

  const getResizedImageUrl = (url: string): string => {
    return isFmicAssetsImage(url) ? `https://www.fender.com/cdn-cgi/image/format=auto,resize=height=auto,width=500/${url}` : `${url}`;
  };

  const showDescription = description && (level == 3 || (level == 2 && isSupportNav));

  let appLinkLabel;
  if (displayType == 'App Link') {
    const contentfulConfig = document.getElementById('contentful-config');
    const siteLanguage = contentfulConfig?.dataset.language || 'en';
    appLinkLabel = appButtonLabelTranslations[siteLanguage as keyof object];
  }

  return (
    <Link
      $brand={brand}
      $displayType={displayType}
      $index={index}
      $hasDescription={!!description}
      $hasImage={imageUrl != ''}
      $isSupportNav={isSupportNav}
      $level={level}
      href={url} 
      onClick={onClick} 
      target={linkTarget}
      {
        ...(level === 1 && {'data-navigation-flyout': title}) 
        || (level === 2 && {'data-navigation-group': title}) 
        || (level === 3 && {'data-navigation-label': title})
      }
    >
      {(imageUrl) && (
        <Image 
          src={getResizedImageUrl(imageUrl)} 
          $isProduct={hasProductImage} 
          $displayType={displayType} 
        />
      )}
      <span dangerouslySetInnerHTML={{ __html: titleToRender }} />
      {(showDescription) && (
        <Description 
          dangerouslySetInnerHTML={{ __html: description }} 
          $displayType={displayType}
        />
      )}
      {(displayType == 'App Link') && <AppLinkButton>{appLinkLabel}</AppLinkButton>}
      {/* Arrow styling varies between brands. */}
      {(subItems && brand == 'jackson') && (
        <PointerRightLight fill="#fff" fontSize="16px" />
      )}
      {(subItems && brand == 'fender') && (
        <PointerRight fill={colors.fender.primaryDark} fontSize="10px" />
      )}
    </Link>
  );
}

export default MobileMenuLink;